import { Component, EventEmitter, Input, Output, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { SafeResourceUrlPipe } from '../../../app/pipes/safe-resource-url.pipe';
import { CollaboraMessageService } from './collabora-message.service';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-collabora-editor',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, MatIconModule, MatButtonModule, SafeResourceUrlPipe,MatTooltipModule],
  templateUrl: './collabora-editor.component.html',
  styleUrls: ['./collabora-editor.component.scss']
})
export class CollaboraEditorComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() documentId?: string;
  @Input() editorUrl?: string; // Nueva opción: URL directa del editor
  @Input() fileName!: string;
  @Input() fileExtension: string = 'docx';
  @Input() accessToken?: string;
  @Input() readOnly: boolean = false;
  @Input() extendedData?: string; // Datos opcionales pasados a WOPI via X-COOL-WOPI-ExtendedData
  
  @Output() editorReady = new EventEmitter<void>();
  @Output() documentSaved = new EventEmitter<void>();
  @Output() error = new EventEmitter<string>();
  @Output() contentChanged = new EventEmitter<void>();
  @Output() focusChange = new EventEmitter<boolean>();
  
  /** Referencia al iframe – se configura vía setter para detectar cuándo aparece en el DOM */
  private _collaboraFrame?: ElementRef<HTMLIFrameElement>;
  @ViewChild('collaboraFrame', { static: false })
  set collaboraFrame(ref: ElementRef<HTMLIFrameElement> | undefined) {
    this._collaboraFrame = ref;
    if (ref) {
      this.setupIframeFocusDetection(ref.nativeElement);
    }
  }
  get collaboraFrame(): ElementRef<HTMLIFrameElement> {
    return this._collaboraFrame!;
  }
  
  isLoading = true;
  errorMessage = '';
  collaboraUrl = '';  
  @Input() allowedOrigins?: string[] = [];
  private messageListener?: (event: MessageEvent) => void;
  private saveResolvers: Array<() => void> = [];  
  private focusPollInterval: any;
  private focusListenersAttached = false;
  public isFocused: boolean = false;
  
  constructor(    
    private collaboraMessageService: CollaboraMessageService
  ) {}
  
  ngOnInit(): void {
    
  }
  
  ngAfterViewInit(): void {    
    this.setupMessageListener();
    this.loadCollaboraEditor();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['editorUrl'] && !changes['editorUrl'].firstChange) {
      this.reloadFromInput();
    }
  }

  /**
   * Configura la detección de foco sobre el iframe.
   * Se invoca automáticamente cuando el iframe aparece en el DOM (vía setter de ViewChild).
   */
  private setupIframeFocusDetection(iframe: HTMLIFrameElement): void {
    if (this.focusListenersAttached) return; // evitar duplicados
    this.focusListenersAttached = true;

    try {
      (iframe as any).tabIndex = 0;
    } catch (_) {}

    iframe.addEventListener('focus', () => this.setFocused(true));
    iframe.addEventListener('blur', () => this.setFocused(false));
    iframe.addEventListener('mousedown', () => this.setFocused(true));

    // Poll document.activeElement como fallback
    this.focusPollInterval = setInterval(() => {
      const activeIsIframe = document.activeElement === iframe;
      if (activeIsIframe !== this.isFocused) {
        this.setFocused(activeIsIframe);
      }
    }, 300);
  }
  
  ngOnDestroy(): void {    
    this.close();
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
    }    
    if (this.focusPollInterval) {
      clearInterval(this.focusPollInterval);
      this.focusPollInterval = null;
    }
  }
  
  private async loadCollaboraEditor(): Promise<void> {    
      this.isLoading = true;
      this.errorMessage = '';
      
      // Si se proporciona editorUrl directamente, usarla
      if (this.editorUrl) {
        this.collaboraUrl = this.editorUrl;
        return;
      }                
  }

  private reloadFromInput(): void {
    this.errorMessage = '';
    this.isLoading = true;
    this.collaboraUrl = '';

    if (!this.editorUrl) {
      return;
    }

    // Forzamos un ciclo de destruccion/creacion del iframe para que cargue la nueva sesion.
    setTimeout(() => {
      this.collaboraUrl = this.editorUrl || '';
    });
  }
  
  private setupMessageListener(): void {
    this.messageListener = (event: MessageEvent) => {

      const msg = this.collaboraMessageService.parseMessage(event, this.allowedOrigins);
      if (!msg) return;            
      
      if (msg.MessageId === 'App_LoadingStatus' && msg.Values.Status === 'Document_Loaded') {
        // Detectar cuando el editor está listo
        this.isLoading = false;
        this.sendCommand('Host_PostmessageReady');        
        this.editorReady.emit();
      }            
            
      if (msg.MessageId === 'Doc_ModifiedStatus') {        
        // Detectar cambios en el contenido
          this.contentChanged.emit();        
      }
            
      if (msg.MessageId === 'Action_Save_Resp') {
        // Detectar guardado           
        this.documentSaved.emit();        
      }            
      
      // Detectar errores
      if (msg.MessageId === 'App_LoadingStatus' && msg.Values?.Status === 'Failed') {
        this.errorMessage = 'Error al cargar el documento';
        this.error.emit(this.errorMessage);
      }
    };
    
    window.addEventListener('message', this.messageListener);
  }

  /**
   * Envía un comando al editor Collabora
   */
  private sendCommand(messageId: string, params?: any): void {        
    this.collaboraMessageService.sendMessage(
      this.collaboraFrame.nativeElement.contentWindow,
      messageId,
      params,
      this.collaboraUrl
    );
  }
  
  /**
   * Guarda el documento manualmente
   * @param terminateEdit Si es true, cierra el documento después de guardar
   */
  public save(terminateEdit: boolean = false): void {
    const params: any = {
      DontTerminateEdit: !terminateEdit,
      DontSaveIfUnmodified: true,
      Notify: true
    };
    if (this.extendedData) params.ExtendedData = this.extendedData;
    this.sendCommand('Action_Save', params);
  }
  
  /**
   * Guarda el documento y espera a que se complete el guardado
   * @returns Promise que se resuelve cuando el documento se ha guardado
   */
  async saveAndWait(): Promise<void> {
    return new Promise((resolve) => {
      
      this.saveResolvers.push(resolve);
      const params: any = {
        DontTerminateEdit: false,
        DontSaveIfUnmodified: true,
        Notify: true
      };
      if (this.extendedData) params.ExtendedData = this.extendedData;
      this.sendCommand('Action_Save', params);
            
      setTimeout(() => {
        const index = this.saveResolvers.indexOf(resolve);
        if (index > -1) {
          this.saveResolvers.splice(index, 1);          
          resolve();
        }
      }, 3000);
    });
  }
  
  /**
   * Activa/desactiva pantalla completa en el editor
   * IMPORTANTE: Debe invocarse directamente desde el handler de click del usuario
   * para que el navegador permita requestFullscreen (requiere user gesture).
   */
  public enterFullscreen(): void {
    const iframe = this.collaboraFrame?.nativeElement;
    if (iframe) {
      const req = iframe.requestFullscreen
        || (iframe as any).webkitRequestFullscreen
        || (iframe as any).msRequestFullscreen;
      if (req) {
        req.call(iframe).catch((err: any) => console.warn('requestFullscreen rechazado:', err));
      }
    }
  }

  /**
   * @deprecated Usar enterFullscreen() en su lugar (requiere user gesture).
   */
  public fullscreen(): void {
    this.enterFullscreen();
  }

  /**
   * Cierra el editor
   */
  public close(): void {
    this.sendCommand('Action_Close');
  }
  
  /**
   * Establece el modo de solo lectura
   */
  public setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly;
    // Nota: Collabora maneja readonly mediante los permisos WOPI
  }
  
  /**
   * Recargar el editor
   */
  public reload(): void {
    this.loadCollaboraEditor();
  }

  public abrirManual(): void {
    if (this.editorUrl) {      
        window.open(this.editorUrl, '_blank');
        return;
      }                
  }

  private setFocused(value: boolean) {
    if (this.isFocused === value) return;
    this.isFocused = value;
    try {
      this.focusChange.emit(this.isFocused);
    } catch (e) {}
  }  
  
}
